"use client";

import React from "react";
import dynamic from "next/dynamic";
import {
  getOptInNewsletterCopy,
  getOptInNewsletter,
  findSiteNameWithoutCom,
  slugRegexMatcher,
} from "../helpers/Helper";
import Sites from "../constants/Sites";
import { montserratFamily } from "helpers/fonts";
import { useAppContext } from "./Provider";
import NextImage from "./Image/NextImage";

const NewsletterV2 = dynamic(() => import("./NewsletterV2/NewsletterV2"), {
  ssr: false,
});

import styles from "./Footer.module.css";

const FooterHeadingText = (siteId) => {
  switch (siteId) {
    case Sites.THE_LINE_UP:
      return "Get our eeriest tales and best book deals delivered straight to your inbox.";
    case Sites.MURDER_MAYHEM:
      return "Get our most mysterious stories and thrilling book deals delivered to your inbox.";
    case Sites.THEPORTALIST:
      return "Sign up to get our favorite sci-fi and fantasy books sent straight to your inbox!";
    case Sites.ALOVESOTRUE:
      return "Sign up for our newsletter to get romance reads delivered to your inbox.";
    case Sites.EARLYBIRDBOOKS:
      return "Get the best daily book deals delivered to your inbox";
    case Sites.EXPLORETHEARCHIVE:
      return "Get historic book deals and news delivered to your inbox";
    case Sites.OPENROADMEDIA:
      return "Get weekly updates and deals from Early Bird Books";
    default:
      return "Get weekly updates and deals delivered to your inbox";
  }
};

const Footer = (props) => {
  const { data, siteId, isMobile, isTablet, router, subscriberCookie } = useAppContext();

  const { funnel_links, business_links, nav_items, text, social_media } =
    data?.SiteSettings?.site_footer || {};
  const year = new Date().getFullYear();
  let optInNewsletterCopy = getOptInNewsletterCopy(siteId);
  let optInNewsletter = getOptInNewsletter(siteId);
  let primaryNewsletter =
    siteId === Sites.OPENROADMEDIA
      ? findSiteNameWithoutCom(Sites.EARLYBIRDBOOKS)
      : findSiteNameWithoutCom(siteId);
  let footerNewsletterProps = {
    ...props,
    // optInNewsletterCopy: optInNewsletterCopy,
    optInNewsletter: {
      optInSite: optInNewsletter,
      optInNewsletterCopy,
      partnerCheckbox: true,
    },
    primaryNewsletter: primaryNewsletter,
  };

  let slug = router?.asPath && router?.asPath?.toString().replace(/%20/g, " ");

  const shouldRenderFooterNl =
    !props.hideNewsletter && !(Sites.EARLYBIRDBOOKS && slug === "/");

  const textBoxInputClass =
    isMobile || isTablet ? styles.mobileInputHeight : styles.desktopInputHeight;
  const buttonBoxButtonClass =
    isMobile || isTablet ? styles.buttonBoxMobileBtn : styles.buttonBoxDestopBtn;

  const renderFooterNL = () => {
    if (props.hideNewsletter) {
      return null;
    }
    // define footer component
    const footer = (
      <>
        <div className={styles.Cpart1}>
          <h2 className={`${styles.footHead} plain`}>{FooterHeadingText(siteId)}</h2>
        </div>
        <div className={styles.Cpart2}>
          <NewsletterV2
            isChild={true}
            Newsletter_Settings={data?.SiteSettings?.newsletter_settings}
            classNames={"footerNewsletter groupButton"}
            {...footerNewsletterProps}
            siteId={siteId}
            textBoxInputClass={textBoxInputClass}
            buttonBoxButtonClass={buttonBoxButtonClass}
          />
        </div>
      </>
    );
    // if we have a cookie or if page slug matches newsletters or signup, dont display
    if (
      subscriberCookie ||
      slugRegexMatcher(slug, "newsletters") ||
      slugRegexMatcher(slug, "signup")
    ) {
      return null;
    } else return footer;
  };

  const renderFooterSocialMedia = () => {
    let facebook, instagram, twitter, pinterest, tiktok;
    if (social_media.facebook) {
      facebook = (
        <li className={styles.inlineItem}>
          <a
            target="_blank"
            rel="noopener noreferrer"
            className="list-inline-link"
            href={social_media.facebook}
          >
            <NextImage
              ImageUrl={`${process.env.S3_IMAGE_URL}/icon_fb.png`}
              Title="Facebook"
              Alt_Text="Facebook"
              width={33}
              height={33}
              blockImage={false}
            />
          </a>
        </li>
      );
    }

    if (social_media.instagram) {
      instagram = (
        <li className={styles.inlineItem}>
          <a
            target="_blank"
            rel="noopener noreferrer"
            className="list-inline-link"
            href={social_media.instagram}
          >
            <NextImage
              ImageUrl={`${process.env.S3_IMAGE_URL}/icon_insta.png`}
              Title="Instagram"
              Alt_Text="Instagram"
              width={33}
              height={33}
              blockImage={false}
            />
          </a>
        </li>
      );
    }

    if (social_media.twitter) {
      twitter = (
        <li className={styles.inlineItem}>
          <a
            target="_blank"
            rel="noopener noreferrer"
            className="list-inline-link"
            href={social_media.twitter}
          >
            <NextImage
              ImageUrl={`${process.env.S3_IMAGE_URL}/icon_x.png`}
              Title="Twitter"
              Alt_Text="Twitter"
              width={33}
              height={33}
              blockImage={false}
            />
          </a>
        </li>
      );
    }

    if (social_media.tiktok) {
      tiktok = (
        <li className={styles.inlineItem}>
          <a
            target="_blank"
            rel="noopener noreferrer"
            className="list-inline-link"
            href={social_media.tiktok}
          >
            <NextImage
              ImageUrl={`${process.env.S3_IMAGE_URL}/icon_tiktok.png`}
              Title="TikTok"
              Alt_Text="TikTok"
              width={33}
              height={33}
              blockImage={false}
            />
          </a>
        </li>
      );
    }

    if (social_media.pinterest) {
      pinterest = (
        <li className={styles.inlineItem}>
          <a
            target="_blank"
            rel="noopener noreferrer"
            className="list-inline-link"
            href={social_media.pinterest}
          >
            <NextImage
              ImageUrl={`${process.env.S3_IMAGE_URL}/icon_pinterest.png`}
              Title="Pinterest"
              Alt_Text="Pinterest"
              width={33}
              height={33}
              blockImage={false}
            />
          </a>
        </li>
      );
    }
    return (
      <React.Fragment>
        <h3 className={styles.siteName}>{"Follow Us"}</h3>
        <div className="mobLayout">
          <ul className={styles.inlineList}>
            {facebook}
            {instagram}
            {twitter}
            {pinterest}
            {tiktok}
          </ul>
        </div>
      </React.Fragment>
    );
  };

  const renderFooterFunnelLinks = () => {
    let funnelLinks;
    if (funnel_links) {
      funnelLinks = funnel_links.map((item, index) => (
        <div className={styles.list} key={index}>
          <a href={item.url} rel="noopener noreferrer" target={item.newTab ? "_blank" : "_self"}>
            {item.text.toUpperCase()}
          </a>
        </div>
      ));
    }

    return (
      <React.Fragment>
        <h4>
          More from
          <span className={styles.headImage}>
            <NextImage
              ImageUrl={`${process.env.S3_IMAGE_URL}/logo.png`}
              Title="OR/M"
              Alt_Text="OR/M"
              width={35}
              height={14}
              className="orm-img"
              blockImage={false}
              style={{ height: "auto", width: "auto" }}
            />
          </span>
        </h4>
        {funnelLinks}
      </React.Fragment>
    );
  };

  const renderFooterCorporate = () => {
    return (
      <React.Fragment>
        <h4>Corporate</h4>
        {business_links
          ? business_links.map((item, index) => (
              <div className={styles.list} key={index}>
                <a href={item.url}>{item.text}</a>
              </div>
            ))
          : null}
        {nav_items
          ? nav_items.map((item, index) => (
              <div className={styles.list} key={index}>
                <a href={item.url}>{item.text.toString().toUpperCase()}</a>
              </div>
            ))
          : null}
      </React.Fragment>
    );
  };

  const renderFooterExtras = () => {
    return (
      <React.Fragment>
        <h4>
          © {year} {text}
        </h4>
        <ul className={styles.inlineList}>
          <li className={styles.inlineItem}>
            <a className={`list-inline-link ${styles.footerInfo}`}>
              We are a participant in the Amazon Services LLC Associates Program, an affiliate
              advertising program designed to provide a means for us to earn fees by linking to
              Amazon.com and affiliated sites.
            </a>
          </li>
        </ul>
      </React.Fragment>
    );
  };

  return (
    <div className={styles.footerParent} style={{ fontFamily: montserratFamily.style.fontFamily }}>
      {shouldRenderFooterNl ? (
        <div className={styles.copyRightDiv}>
          <div className={styles.copyRight}>{renderFooterNL()}</div>
        </div>
      ) : null}
      <div className={styles.footerDiv}>
        <div className={styles.footer}>
          <div className={styles.part1}>{social_media ? renderFooterSocialMedia() : null}</div>
          <div className={styles.part2}>{renderFooterFunnelLinks()}</div>
          <div className={styles.part3}>{renderFooterCorporate()}</div>
          <div className={styles.part4}>{renderFooterExtras()}</div>
        </div>
      </div>
    </div>
  );
};

export default Footer;
